
import React, { useState, useEffect } from 'react';
import { Bus, User, Users, CheckCircle2, Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import axiosInstance from '@/utils/axiosInstance';
import { API_ENDPOINTS } from '@/constants/config';

const Step2DestinationBus = ({ wizardData, updateWizardData }) => {
  const [buses, setBuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBuses();
  }, []);

  const fetchBuses = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosInstance.get(API_ENDPOINTS.vehicles, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      
      const vehiclesData = response.data?.data?.vehicles || [];
      
      // Mock additional data for demonstration
      const busesWithData = vehiclesData.map((vehicle) => ({
        id: vehicle._id,
        busNumber: vehicle.vehicleNumber,
        type: vehicle.type || 'bus',
        driver: vehicle.driver?.name || 'Not Assigned',
        studentCount: 22, // Fixed mock data to prevent capacity warnings
        capacity: vehicle.seatingCapacity || 50,
        status: vehicle.locationSharingActive ? 'active' : 'inactive',
      }));
      
      setBuses(busesWithData);
    } catch (err) {
      console.error('Error fetching buses:', err);
      setError('Failed to load buses. Please try again.');
      // Fallback to mock data for demo
      setBuses([
        {
          id: '1',
          busNumber: 'BUS-001',
          type: 'bus',
          driver: 'John Doe',
          studentCount: 22,
          capacity: 50,
          status: 'active',
        },
        {
          id: '2',
          busNumber: 'BUS-002',
          type: 'bus',
          driver: 'Jane Smith',
          studentCount: 18,
          capacity: 45,
          status: 'active',
        },
        {
          id: '3',
          busNumber: 'BUS-003',
          type: 'van',
          driver: 'Mike Johnson',
          studentCount: 10,
          capacity: 20,
          status: 'inactive',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectBus = (bus) => {
    updateWizardData('destinationBus', bus);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600">Loading buses...</span>
      </div>
    );
  }

  if (error && buses.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={fetchBuses}
          className="text-blue-600 hover:text-blue-700 font-medium"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <p className="text-gray-600 mb-2">
          Select the bus that will physically operate and carry all students. This is the destination
          bus for the merge.
        </p>
        <p className="text-sm text-gray-500">
          Students from source buses will be transferred to this bus's route.
        </p>
      </div>

      {buses.length === 0 ? (
        <div className="text-center py-12">
          <Bus className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No buses available</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {buses.map((bus) => {
            const isSelected = wizardData.destinationBus?.id === bus.id;
            const utilizationPercent = Math.round((bus.studentCount / bus.capacity) * 100);

            return (
              <Card
                key={bus.id}
                onClick={() => handleSelectBus(bus)}
                className={`p-4 cursor-pointer transition-all hover:shadow-md ${
                  isSelected
                    ? 'border-2 border-blue-600 bg-blue-50'
                    : 'border border-gray-200 hover:border-blue-300'
                }`}
              >
                <div className="space-y-3">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Bus className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{bus.busNumber}</h3>
                        <p className="text-xs text-gray-500 capitalize">{bus.type}</p>
                      </div>
                    </div>
                    {isSelected && (
                      <CheckCircle2 className="h-5 w-5 text-blue-600" />
                    )}
                  </div>

                  {/* Driver */}
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <User className="h-4 w-4" />
                    <span>{bus.driver}</span>
                  </div>

                  {/* Capacity */}
                  <div className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Users className="h-4 w-4" />
                        <span>
                          {bus.studentCount} / {bus.capacity} students
                        </span>
                      </div>
                      <span className="text-xs font-medium text-gray-500">
                        {utilizationPercent}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${
                          utilizationPercent > 90
                            ? 'bg-red-500'
                            : utilizationPercent > 70
                            ? 'bg-yellow-500'
                            : 'bg-green-500'
                        }`}
                        style={{ width: `${utilizationPercent}%` }}
                      />
                    </div>
                  </div>

                  {/* Status */}
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        bus.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full mr-1 ${
                          bus.status === 'active' ? 'bg-green-500' : 'bg-gray-500'
                        }`}
                      />
                      {bus.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {wizardData.destinationBus && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Selected:</strong> {wizardData.destinationBus.busNumber} will be the
            destination bus. All students will be transferred to this bus's route.
          </p>
        </div>
      )}
    </div>
  );
};

export default Step2DestinationBus;
